"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { trackEvent } from "@/lib/track";
import { CustomLink } from "../link/link.comp";
import { StyledDemo } from "./chatbotDemo.styles";
import {
  DEMO_BOTS,
  IDemoAnswer,
  IDemoProductCard,
  OFF_SCRIPT_REPLY,
} from "./chatbotDemo.data";

interface IChatbotDemoProps {
  editorHref: string;
  eventPrefix?: string;
}

interface IMessage {
  id: number;
  role: "bot" | "user";
  /** Text currently visible. Grows while the reply streams in. */
  text: string;
  /** Full reply. Present only while a bot message is still streaming. */
  full?: string;
  cards?: IDemoProductCard[];
  action?: { label: string };
}

const TYPING_DELAY = 620;
const WORD_INTERVAL = 26;

const GLYPHS: Record<IDemoProductCard["glyph"], React.ReactNode> = {
  jacket: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 3l3 2 3-2 4 2.5-1.5 4 1.5.8V21H5V10.3l1.5-.8L5 5.5 9 3z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M12 5v7" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  boots: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M7 3h4v8l6 3.5c1.2.7 2 2 2 3.4V21H7V3z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M7 17h12" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
};

/**
 * Talk-to-it demo panel: four sample-site bots with hand-written answers, so
 * it costs nothing per visitor and cannot fail. Rendered inside the standard
 * WidgetHeroLight through its `heroVisual` slot.
 */
export const ChatbotDemo = ({
  editorHref,
  eventPrefix = "ai-chatbot",
}: IChatbotDemoProps) => {
  const [botIndex, setBotIndex] = useState(0);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [typing, setTyping] = useState(false);
  const [askedIds, setAskedIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [converted, setConverted] = useState(false);

  const bot = DEMO_BOTS[botIndex];
  const threadRef = useRef<HTMLDivElement>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const msgId = useRef(0);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  const later = useCallback((fn: () => void, ms: number) => {
    timers.current.push(setTimeout(fn, ms));
  }, []);

  // Keep the newest message in view without scrolling the page itself.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, typing]);

  const streamReply = useCallback(
    (reply: Pick<IDemoAnswer, "answer" | "cards" | "action">) => {
      const words = reply.answer.split(" ");
      msgId.current += 1;
      const id = msgId.current;

      setTyping(false);
      setMessages((prev) => [
        ...prev,
        { id, role: "bot", text: "", full: reply.answer },
      ]);

      words.forEach((_, i) => {
        later(() => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === id
                ? { ...m, text: words.slice(0, i + 1).join(" ") }
                : m
            )
          );
        }, WORD_INTERVAL * (i + 1));
      });

      const settle = WORD_INTERVAL * (words.length + 1);

      later(() => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === id
              ? {
                  ...m,
                  text: reply.answer,
                  full: undefined,
                  cards: reply.cards,
                  action: reply.action,
                }
              : m
          )
        );
        setBusy(false);
      }, settle);
    },
    [later]
  );

  // Reset the transcript whenever the visitor picks a different demo bot, then
  // play the bot's opening exchange so the panel is never an empty box.
  useEffect(() => {
    clearTimers();
    msgId.current += 1;
    setMessages([{ id: msgId.current, role: "bot", text: bot.greeting }]);
    setTyping(false);
    setAskedIds([]);
    setDraft("");

    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    if (reduced) {
      msgId.current += 1;
      const q = msgId.current;
      msgId.current += 1;
      const a = msgId.current;
      setMessages((prev) => [
        ...prev,
        { id: q, role: "user", text: bot.opener.question },
        { id: a, role: "bot", text: bot.opener.answer },
      ]);
      setBusy(false);
      return clearTimers;
    }

    setBusy(true);
    later(() => {
      msgId.current += 1;
      setMessages((prev) => [
        ...prev,
        { id: msgId.current, role: "user", text: bot.opener.question },
      ]);
      setTyping(true);
      later(() => streamReply(bot.opener), TYPING_DELAY);
    }, 700);

    return clearTimers;
  }, [botIndex, bot.greeting, bot.opener, clearTimers, later, streamReply]);

  const ask = useCallback(
    (question: string, reply: Pick<IDemoAnswer, "answer" | "cards" | "action">) => {
      if (busy) return;
      setBusy(true);
      msgId.current += 1;
      setMessages((prev) => [
        ...prev,
        { id: msgId.current, role: "user", text: question },
      ]);
      setTyping(true);
      later(() => streamReply(reply), TYPING_DELAY);
    },
    [busy, later, streamReply]
  );

  const onPrompt = (answer: IDemoAnswer) => {
    trackEvent(`${eventPrefix}_demo_question_click`, {
      bot: bot.id,
      question: answer.question,
    });
    setAskedIds((prev) => [...prev, answer.question]);
    setConverted(true);
    ask(answer.question, answer);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    trackEvent(`${eventPrefix}_demo_freetext`, { bot: bot.id });
    setDraft("");
    setConverted(true);
    ask(text, { answer: OFF_SCRIPT_REPLY });
  };

  const onSwitchBot = (i: number) => {
    if (i === botIndex) return;
    trackEvent(`${eventPrefix}_demo_bot_switch`, { bot: DEMO_BOTS[i].id });
    setBotIndex(i);
  };

  const remaining = bot.answers.filter((a) => !askedIds.includes(a.question));

  return (
    <StyledDemo style={{ ["--bot-accent" as string]: bot.accent }}>
      <div className="demo-label">
        <span>Live demo</span>
        <span className="live">
          <span className="pulse" aria-hidden="true" />
          Sample sites, scripted answers
        </span>
      </div>

      <div className="tabs" role="tablist" aria-label="Pick a demo chatbot">
        {DEMO_BOTS.map((b, i) => (
          <button
            key={b.id}
            type="button"
            role="tab"
            className="tab"
            aria-selected={i === botIndex}
            onClick={() => onSwitchBot(i)}
          >
            {b.tab}
          </button>
        ))}
      </div>

      <div className="bot-head">
        <span className="avatar" aria-hidden="true">
          {bot.initials}
        </span>
        <span>
          <span className="name">{bot.siteName}</span>
          <span className="knowledge">{bot.knowledge}</span>
        </span>
      </div>

      <div className="thread" ref={threadRef} aria-live="polite">
        {messages.map((m) => (
          <React.Fragment key={m.id}>
            <div className={`msg ${m.role}`}>
              {m.text}
              {m.full ? <span className="caret" aria-hidden="true" /> : null}
            </div>
            {m.cards ? (
              <div className="cards">
                {m.cards.map((c) => (
                  <div className="pcard" key={c.title}>
                    <span className="thumb">{GLYPHS[c.glyph]}</span>
                    <span>
                      <span className="title">{c.title}</span>
                      <span className="meta">{c.meta}</span>
                    </span>
                    <span className="price">{c.price}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {m.action ? (
              <button type="button" className="inline-action" tabIndex={-1}>
                {m.action.label}
              </button>
            ) : null}
          </React.Fragment>
        ))}
        {typing ? (
          <div className="typing" aria-label="Typing">
            <span />
            <span />
            <span />
          </div>
        ) : null}
      </div>

      {remaining.length > 0 ? (
        <div className="prompts">
          {remaining.map((a) => (
            <button
              key={a.question}
              type="button"
              className="prompt"
              disabled={busy}
              onClick={() => onPrompt(a)}
            >
              {a.question}
            </button>
          ))}
        </div>
      ) : null}

      <form className="composer" onSubmit={onSubmit}>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={`Ask ${bot.siteName} something else`}
          aria-label={`Ask the ${bot.siteName} demo chatbot a question`}
        />
        <button type="submit" disabled={busy || !draft.trim()}>
          Send
        </button>
      </form>

      {converted ? (
        <div className="convert">
          <p>Now point one at your own site.</p>
          <CustomLink
            variant="gradient"
            external
            href={editorHref}
            title="Create your AI Chatbot"
            onClick={() => trackEvent(`${eventPrefix}_demo_cta_click`)}
          >
            Create yours
          </CustomLink>
        </div>
      ) : null}
    </StyledDemo>
  );
};
